import React, { useState, useEffect, useRef, useContext } from "react";
import { ApplicationContext } from "../utils/contextsProviders";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  useToast,
  Spinner,
  Badge,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuList,
  CheckboxGroup,
  Checkbox,
  Select,
  MenuItem,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Text,
} from "@chakra-ui/react";
import axios from "axios";
import { useCountries } from 'use-react-countries';
import CustomerAdminFilesStep from "./CustomerAdminFilesStep";

const allStates = {
  "Australia": [
    "NSW - New South Wales",
    "QLD - Queensland",
    "VIC - Victoria",
    "SA - South Australia",
    "TAS - Tasmania",
    "WA - Western Australia",
    "NT - Northern Territory"
  ],
  "Belgium": [
    "FR - Flemish Region",
    "WR - Walloon Region",
    "BCR - Brussels-Capital Region"
  ]
};

const CustomerUsers = () => {

  const toast = useToast();
  const { user } = useContext(ApplicationContext)
  const { id: adminId } = user

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState(["Australia"]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [newUser, setNewUser] = useState({
    userName: "",
    email: "",
    phoneNumber: "",
    password: "",
    companyName: user.role === "CustomerAdmin" ? user.companyName : "",
    countries: ["Australia"],
    states: [],
    locations: [],
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [step, setStep] = useState(1);
  const [filesStepCompanyName, setFilesStepCompanyName] = useState(null);
  const { countries } = useCountries();
  const sortedCountries = [...countries].sort((a, b) => a.name.localeCompare(b.name));
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState(null);
  const cancelRef = useRef();
  const replaceFileInputRefs = useRef({});

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.role==="SuperAdmin" ? "all" : `admin/${adminId}`}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      )
      .then((response) => {
        setUsers(user.role === "SuperAdmin" ? response.data : response.data.customerUsers);
        setLoadingUsers(false);
      })
      .catch((error) => {
        console.error("Error fetching company users:", error);
        setLoadingUsers(false);
      });

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/smappee/service-locations`)
      .then((response) => {
        const locationData = response.data.serviceLocations.map(
          (location) => ({
            serviceLocationId: location.serviceLocationId,
            name: location.name,
          })
        );
        setLocations(locationData.filter(
            (location) => !location.name.includes("CABIN") && !location.name.includes("Elvy") && !location.name.includes("Gray") && !location.name.includes("Josh") && !location.name.includes("Shops")
          ).map(location => ({
            ...location,
            state: "NSW - New South Wales"
          }))
        );
      })
      .catch((error) => {
        console.error("Error fetching locations:", error);
      });
  }, [adminId]);

  const handleChange = (e) => {
    setNewUser({ ...newUser, [e.target.name]: e.target.value });
  };

  const handleCountryChange = (selectedCountriesArray) => {
    setSelectedCountries(selectedCountriesArray);
    setNewUser({ ...newUser, countries: selectedCountriesArray });
  };

  const handleSubmit = () => {
    if (user.role === "CustomerAdmin") {
      const loggedInUserDomain = user.email.split('@')[1];
      const newUserDomain = newUser.email.split('@')[1];

      if (loggedInUserDomain !== newUserDomain) {
        toast({
          title: "Error.",
          description: "You can't add a user with a different email domain. Please use your company email.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    if (isEditMode) {
      // Update existing user
      const userData = { ...newUser };

      // Only include password if it's not empty
      if (!userData.password || userData.password.trim() === '') {
        delete userData.password;
      }
      
      axios
        .put(`${process.env.REACT_APP_BACKEND_URL}/api/users/${editingUserId}`, userData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        .then((response) => {
          toast({
            title: "User updated.",
            description: "The user has been updated successfully.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          refetchUsers();
          onClose();
          resetForm();
        })
        .catch((error) => {
          console.error("Error updating user:", error);
          toast({
            title: "Error.",
            description: "There was an error updating the user.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
    } else {
      // Create new user
      const userData = { ...newUser };
      if (user.role === "CustomerAdmin") {
        userData.adminId = adminId;
      }

      axios
        .post(`${process.env.REACT_APP_BACKEND_URL}/api/users/register`, userData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        .then((response) => {
          toast({
            title: "User registered.",
            description: "The user has been registered successfully.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          refetchUsers();

          if (user.role === "SuperAdmin") {
            // Move to step 2: manage files for the newly created Customer Admin's company
            setFilesStepCompanyName(newUser.companyName);
            setStep(2);
          } else {
            onClose();
            resetForm();
          }
        })
        .catch((error) => {
          console.error("Error registering user:", error);
          toast({
            title: "Error.",
            description: "There was an error registering the user.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
    }
  };

  const handleStateChange = (selectedStates) => {
    setSelectedStates(selectedStates);
    setNewUser({ ...newUser, states: selectedStates });
  };

  const handleLocationChange = (selectedLocations) => {
    setSelectedLocations(selectedLocations);
    setNewUser({ ...newUser, locations: selectedLocations });
  };

  const handleEdit = (userToEdit) => {
    setIsEditMode(true);
    setEditingUserId(userToEdit._id);
    
    // Set the countries first
    const userCountries = userToEdit.countries || ["Australia"];
    setSelectedCountries(userCountries);
    
    // Set the states
    const userStates = userToEdit.states || [];
    setSelectedStates(userStates);
    
    // Set the locations
    const userLocations = userToEdit.locations || [];
    setSelectedLocations(userLocations);
    
    setNewUser({
      userName: userToEdit.userName || "",
      email: userToEdit.email || "",
      phoneNumber: userToEdit.phoneNumber || "",
      password: "", // Don't populate password for editing
      companyName: userToEdit.companyName || "",
      countries: userCountries,
      states: userStates,
      locations: userLocations,
    });
    
    onOpen();
  };

  const resetForm = () => {
    setIsEditMode(false);
    setEditingUserId(null);
    setNewUser({
      userName: "",
      email: "",
      phoneNumber: "",
      password: "",
      companyName: user.role === "CustomerAdmin" ? user.companyName : "",
      countries: ["Australia"],
      states: [],
      locations: [],
    });
    setSelectedCountries(["Australia"]);
    setSelectedStates([]);
    setSelectedLocations([]);
    setStep(1);
    setFilesStepCompanyName(null);
  };

  const handleClose = () => {
    onClose();
    resetForm();
    refetchUsers();
  };

  // Function to refetch all users
  const refetchUsers = () => {
    setLoadingUsers(true);
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.role==="SuperAdmin" ? "all" : `admin/${adminId}`}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      )
      .then((response) => {
        setUsers(user.role === "SuperAdmin" ? response.data : response.data.customerUsers);
        setLoadingUsers(false);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
        setLoadingUsers(false);
      });
  };

  const handleDelete = (userId) => {
    setUserIdToDelete(userId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    axios
      .delete(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userIdToDelete}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then((response) => {
        toast({
          title: "User Delete.",
          description: "The user has been deleted successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setUsers((prevUsers) =>
          prevUsers.filter((user) => user._id !== userIdToDelete)
        );
        setIsDeleteDialogOpen(false);
        setUserIdToDelete(null);
        onClose();
      })
      .catch((error) => {
        console.error("Error deleting user:", error);
        toast({
          title: "Error.",
          description: "There was an error deleting the user.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setIsDeleteDialogOpen(false);
        setUserIdToDelete(null);
      });
  };

  // Group users by company name (excluding superadmin@test.com)
  const groupedUsers = users
    .filter((user) => user.email && user.email !== "superadmin@test.com")
    .reduce((acc, user) => {
      const companyName = user.companyName || 'Unknown';
      if (!acc[companyName]) acc[companyName] = [];
      acc[companyName].push(user);
      return acc;
    }, {});

  return (
    <Box p={4}>
      <Flex justifyContent={"space-between"} alignItems={"center"}>
        <Heading fontSize={20}>{`Users`}</Heading>
        <Button
          background={"grey"}
          color={"white"}
          borderRadius={"sm"}
          _hover={{ color: "black" }}
          onClick={onOpen}
        >
          {user.role === "SuperAdmin" ? 'Add Customer Admin' : 'Add Customer User'}
        </Button>
      </Flex>

      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent maxW="900px" width="100%">
          <ModalHeader>
            {step === 2
              ? `Manage Files - ${filesStepCompanyName}`
              : isEditMode
              ? `Edit ${user.role==="SuperAdmin" ? 'Customer Admin' : 'Customer User'}`
              : user.role==="SuperAdmin" ? 'Add Customer Admin' : 'Add Customer User'
            }
          </ModalHeader>
          <ModalCloseButton />
          {step === 2 ? (
            <ModalBody>
              <CustomerAdminFilesStep companyName={filesStepCompanyName} />
            </ModalBody>
          ) : (
          <ModalBody display={"grid"} gridTemplateColumns="repeat(2, 1fr)" gap={4} >
            {user.role!=="CustomerAdmin"&&<FormControl>
              <FormLabel>Company Name <span style={{ color: 'red' }}>*</span></FormLabel>
              <Input
                type="text"
                name="companyName"
                value={newUser.companyName}
                onChange={handleChange}
                required={true}
              />
            </FormControl>}
            <FormControl>
              <FormLabel>User Name <span style={{ color: 'red' }}>*</span></FormLabel>
              <Input
                type="text"
                name="userName"
                value={newUser.userName}
                onChange={handleChange}
                required={true}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Email <span style={{ color: 'red' }}>*</span></FormLabel>
              <Input
                type="email"
                name="email"
                value={newUser.email}
                onChange={handleChange}
                required={true}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Phone Number <span style={{ color: 'red' }}>*</span></FormLabel>
              <Input
                type="tel"
                name="phoneNumber"
                value={newUser.phoneNumber}
                onChange={handleChange}
                required={true}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Password {!isEditMode && <span style={{ color: 'red' }}>*</span>}</FormLabel>
              <Input
                name="password"
                type="password"
                value={newUser.password}
                onChange={handleChange}
                required={!isEditMode}
                placeholder={isEditMode ? "Leave blank to keep current password" : ""}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Country(s) <span style={{ color: 'red' }}>*</span></FormLabel>
              <Menu matchWidth closeOnSelect={false}>
                <MenuButton as={Button} width={'100%'}>
                  Select Countries
                </MenuButton>
                <MenuList style={{display:'flex', flexDirection:"column", padding:"5px 10px", height:"100px", overflowY:"auto", gap:"5px"}}>
                  <CheckboxGroup
                    colorScheme="blackAlpha"
                    onChange={handleCountryChange}
                    value={selectedCountries}
                  >
                    {sortedCountries.map(({name}) => (
                      <Checkbox alignItems="flex-start" sx={{ '.chakra-checkbox__label': { marginTop: '-5px' } }} value={name} fontWeight={name==="Australia" || name ==="Belgium"?"700":""}>{name}</Checkbox>
                    ))}
                  </CheckboxGroup>
                </MenuList>
              </Menu>
            </FormControl>
            {selectedCountries.length > 0 ?
            <>
            <FormControl>
              <FormLabel>States <span style={{ color: 'red' }}>*</span></FormLabel>
                              <Menu matchWidth>
                  <MenuButton as={Button} width={'100%'}>Select States</MenuButton>
                  <MenuList display={'flex'} padding={'9px'} flexDirection={'column'} gap="5px">
                    <CheckboxGroup
                      colorScheme="blackAlpha"
                      onChange={handleStateChange}
                      value={selectedStates}
                    >
                      {selectedCountries.includes('Australia') && (
                        <>
                          <Text fontWeight="bold" px={2} mt={1}>Australia</Text>
                          {allStates['Australia'].map((state) => (
                            <Checkbox alignItems="flex-start" sx={{ '.chakra-checkbox__label': { marginTop: '-5px' } }} key={`AU-${state}`} value={state}>{state}</Checkbox>
                          ))}
                        </>
                      )}
                      {selectedCountries.includes('Belgium') && (
                        <>
                          <Text fontWeight="bold" px={2} mt={2}>Belgium</Text>
                          {allStates['Belgium'].map((state) => (
                            <Checkbox alignItems="flex-start" key={`BE-${state}`} value={state}>{state}</Checkbox>
                          ))}
                        </>
                      )}
                    </CheckboxGroup>
                  </MenuList>
                </Menu>
            </FormControl>
            {selectedStates.length>0 && locations.filter((location) => true).length? 
            <FormControl>
              <FormLabel>Locations <span style={{ color: 'red' }}>*</span></FormLabel>
              <Menu matchWidth>
                <MenuButton as={Button} width={'100%'}>Select Locations</MenuButton>
                <MenuList display={'flex'} padding={'9px'} flexDirection={'column'} height="140px" overflowY="auto" gap="5px">
                  <CheckboxGroup
                    colorScheme="blackAlpha"
                    onChange={handleLocationChange}
                    value={selectedLocations}
                  >
                    {(
                      (user.role === "CustomerAdmin" ? user.locations : locations)
                    )
                      ?.filter(location => {
                        return location.state && selectedStates.some(state => state.includes((location.state || '').split(" - ")[0]));
                      })
                      .map((location) => (
                        <Checkbox alignItems="flex-start" sx={{ '.chakra-checkbox__label': { marginTop: '-5px' } }} key={user.role === "CustomerAdmin" ? location : location.name} value={user.role === "CustomerAdmin" ? location : location.name}>{user.role === "CustomerAdmin" ? location : location.name}</Checkbox>
                      ))}
                  </CheckboxGroup>
                </MenuList>
              </Menu>
            </FormControl>
            :
            selectedStates.length > 0 && <p style={{"margin-top":"10px"}}>No locations found for these states</p>
            }
            </>:
            <p style={{"margin-top":"10px"}}>No Countries selected</p>
            }
          </ModalBody>
          )}
          <ModalFooter>
            {step === 2 ? (
              <Button
                colorScheme="teal"
                borderRadius={"sm"}
                onClick={handleClose}
              >
                Finish
              </Button>
            ) : (
              <Button
                colorScheme="teal"
                borderRadius={"sm"}
                onClick={handleSubmit}
              >
                {isEditMode ? 'Update' : (user.role === "SuperAdmin" ? 'Next' : 'Submit')}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box mt={4}>
        {loadingUsers ? (
          <Spinner />
        ) : user.role === "SuperAdmin" ? (
          Object.entries(groupedUsers).map(([companyName, domainUsers]) => (
            <Box key={companyName} mb={8}>
              <Heading size="md" mb={2}>{companyName}</Heading>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Username</Th>
                    <Th>Email</Th>
                    <Th>Phone</Th>
                    <Th>Countries</Th>
                    <Th>States</Th>
                    <Th>Locations</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {domainUsers.map((user) => (
                    <Tr key={user._id}>
                      <Td>{user.userName}</Td>
                      <Td>{user.email}</Td>
                      <Td>{user.phoneNumber}</Td>
                      <Td>{user.countries ? user.countries.join(', ') : 'N/A'}</Td>
                      <Td>
                        <Menu matchWidth>
                          <MenuButton as={Button} width={'100%'}>
                            View States
                          </MenuButton>
                          <MenuList>
                            {user.states && user.states.length > 0 ? (
                              user.states.map((state) => (
                                <MenuItem key={state}>{state}</MenuItem>
                              ))
                            ) : (
                              <MenuItem>No States</MenuItem>
                            )}
                          </MenuList>
                        </Menu>
                      </Td>
                      <Td>
                        <Menu matchWidth>
                          <MenuButton as={Button} width={'100%'}>
                            View Locations
                          </MenuButton>
                          <MenuList>
                            {user.locations && user.locations.length > 0 ? (
                              user.locations.map((location) => (
                                <MenuItem key={location}>{location}</MenuItem>
                              ))
                            ) : (
                              <MenuItem>No Locations</MenuItem>
                            )}
                          </MenuList>
                        </Menu>
                      </Td>
                      <Td>
                        <Flex gap={2}>
                          <Button
                            colorScheme="blue"
                            borderRadius={"sm"}
                            size="sm"
                            onClick={() => handleEdit(user)}
                          >
                            Edit
                          </Button>
                          <Button
                            colorScheme="red"
                            borderRadius={"sm"}
                            size="sm"
                            onClick={() => handleDelete(user._id)}
                          >
                            Delete
                          </Button>
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ))
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Username</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Countries</Th>
                <Th>States</Th>
                <Th>Locations</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((user) => (
                <Tr key={user._id}>
                  <Td>{user.userName}</Td>
                  <Td>{user.email}</Td>
                  <Td>{user.phoneNumber}</Td>
                  <Td>{user.countries ? user.countries.join(', ') : 'N/A'}</Td>
                  <Td>
                    <Menu>
                      <MenuButton as={Button} width={'100%'}>
                        View States
                      </MenuButton>
                      <MenuList>
                        {user.states && user.states.length > 0 ? (
                          user.states.map((state) => (
                            <MenuItem key={state}>{state}</MenuItem>
                          ))
                        ) : (
                          <MenuItem>No States</MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton as={Button} width={'100%'}>
                        View Locations
                      </MenuButton>
                      <MenuList>
                        {user.locations && user.locations.length > 0 ? (
                          user.locations.map((location) => (
                            <MenuItem key={location}>{location}</MenuItem>
                          ))
                        ) : (
                          <MenuItem>No Locations</MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                                        <Td>
                        <Flex gap={2}>
                          <Button
                            colorScheme="blue"
                            borderRadius={"sm"}
                            size="sm"
                            onClick={() => handleEdit(user)}
                          >
                            Edit
                          </Button>
                          <Button
                            colorScheme="red"
                            borderRadius={"sm"}
                            size="sm"
                            onClick={() => handleDelete(user._id)}
                          >
                            Delete
                          </Button>
                        </Flex>
                      </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete User
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this user? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default CustomerUsers;